import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChevronDown,
  faDollarSign,
  faMagnifyingGlass,
  faRulerCombined,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import ListingCard from "../components/listingCard.component";
import Pagination from "../components/pagination.component";
import BouncingDotsLoader from "./loader.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { formatStr } from "../functions/listings";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function CommercialListings() {
  const { state } = useLocation();
  const seoConfig = pageSEO.listings();
  const maxListings = 15;
  const [listings, setListings] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("All");
  const [error, setError] = useState("");
  const commercialRef = useRef(null);

  //dummy listings
  const demoListings = data.value.filter(
    listing =>
      (listing.PropertyType || "").toLowerCase().includes("commercial")
  );

  const [searchParams, setSearchParams] = useState({
    type: "",
    sqft: "",
    minPrice: "",
    maxPrice: "",
  });

  /* API REQUEST*/
  async function fetchData(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };
    const response =
      constants.ENVIRONMENT === "production"
        ? await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw Error("API ERROR");
    return response.json();
  }

  /* FORMAT IMAGES */
  function formatImages(media) {
    return media.reduce((acc, item) => {
      if (!acc[item.ResourceRecordKey])
        acc[item.ResourceRecordKey] = [];
      acc[item.ResourceRecordKey].push(item);
      return acc;
    }, {});
  }

  /* LOAD MEDIA */
  async function loadImages(data) {
    if (!data.length) return;

    const ids = data
      .map(item => `'${item.ListingKey}'`)
      .join(",");

    let url;

    if (constants.ENVIRONMENT === "production") {
      url =
        `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(${ids})(S)and(S)ImageSizeDescription(S)eq(S)'Thumbnail'(S)and(S)PreferredPhotoYN(S)eq(S)true`;
    } else {
      url =
        `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (${ids}) and ImageSizeDescription eq 'Thumbnail' and PreferredPhotoYN eq true`;
    }

    const result = await fetchData(url);

    setImages(formatImages(result.value));
  }

  /* LOAD LISTINGS */
  async function loadListings() {
    try {
      setLoading(true);

      let filterQuery =
        "contains(PropertyType, 'Commercial')";

      if (filter === "Office") {
        filterQuery +=
          ` and ListOfficeKey eq '${constants.OFFICE_ID}'`;
      }

      Object.values(searchParams)
        .filter(Boolean)
        .forEach(item => {
          filterQuery +=
            ` and ${item}`;
        });

      // Format only for production
      const finalFilter =
        constants.ENVIRONMENT === "production"
          ? formatStr(filterQuery)
          : filterQuery;

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=${filterQuery}(A)$orderby=OriginalEntryTimestamp(S)desc&$top=500`
        :
        `https://query.ampre.ca/odata/Property?$filter=${filterQuery}&$orderby=OriginalEntryTimestamp desc&$top=500`;

      const data =
        await fetchData(url);

      console.log("data", data);

      if (data.value) {
        setListings(data.value);

        await loadImages(
          data.value.slice(
            0,
            maxListings
          )
        );
        setError("");
      }
    }
    catch (error) {
      console.log(error);
      setListings([]);
      setImages({});

      // setError(
      //   "Unable to load listings."
      // );
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    console.log("FILTER", filter)
    loadListings()
  }, [filter, searchParams]);

  useEffect(() => {
    async function updatePageImages() {
      const data =
        listings.slice(
          (page - 1) * maxListings,
          page * maxListings
        );

      await loadImages(data);
    }
    if (listings.length) {
      updatePageImages();
    }
  }, [page]);

  /* SEARCH UPDATE */
  function updateSearch(key, value) {
    setSearchParams(prev => ({
      ...prev,
      [key]: value
    }));
  }

  function handleSearch() {
    setPage(1);
  }

  function clearSearch() {
    setSearchParams({
      type: "",
      sqft: "",
      minPrice: "",
      maxPrice: "",
    });

    setFilter("All");
    setPage(1);
  }

  /* PAGINATION */
  // const totalPages =
  //   Math.ceil(
  //     listings.length / maxListings
  //   );

  // const visibleListings =
  //   listings.slice(
  //     (page - 1) * maxListings,
  //     page * maxListings
  //   );

  const totalPages =
    Math.ceil(
      demoListings.length / maxListings
    );

  const visibleListings =
    demoListings.slice(
      (page - 1) * maxListings,
      page * maxListings
    );

  const handlePageChange = (newPage) => {
    setPage(newPage);

    if (commercialRef.current) {
      commercialRef.current.scrollIntoView({
        block: "start",
      });
    }
  };

  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative overflow-hidden bg-[#fffdfa] pt-36 pb-24">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto max-w-[1250px] px-6 lg:px-16"
        >
          {/* HERO HEADER */}
          <motion.div
            variants={fadeUp}
            className="border-b border-[#8b8172]/30 pb-12"
          >
            <div className="flex items-center gap-5 mb-8">
              <div className="h-[1px] w-16 bg-[#8b8172]" />

              <p className="uppercase tracking-[0.45em] text-xs text-[#6d655b]">
                Commercial Property Search
              </p>
            </div>

            <h1 className="font-serif text-5xl lg:text-6xl leading-none text-[#2b2925]">
              Commercial Space
            </h1>
          </motion.div>

          {/* SEARCH PANEL */}
          <motion.div
            variants={fadeUp}
            className="mt-14 bg-white border-[15px] border-[#8b8172]/30 p-6 lg:py-12 shadow-xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
              {/* Bedrooms */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8b8172]"
                />

                <select
                  value={searchParams.type}
                  onChange={(e) => updateSearch("type", e.target.value)}
                  className="h-14 w-full appearance-none border border-[#8b8172]/30 pl-11 px-5 bg-[#fffdfa] text-[#2b2925] outline-none focus:border-[#8b8172]"
                >
                  <option value="">Property Type</option>

                  <option value="contains(PropertySubType, 'Office')">
                    Office
                  </option>

                  <option value="contains(PropertySubType, 'Retail')">
                    Retail
                  </option>

                  <option value="contains(PropertySubType, 'Investment')">
                    Investment
                  </option>

                  <option value="contains(PropertySubType, 'Industrial')">
                    Industrial
                  </option>
                </select>

                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                />
              </div>

              {/* Bathrooms */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faRulerCombined}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8b8172]"
                />

                <select
                  value={searchParams.sqft}
                  onChange={(e) => updateSearch("sqft", e.target.value)}
                  className="h-14 w-full appearance-none border border-[#8b8172]/30 pl-11 px-5 bg-[#fffdfa] text-[#2b2925] outline-none focus:border-[#8b8172]"
                >
                  <option value="">Square Footage</option>

                  <option value="BuildingAreaTotal lt 500">
                    Under 500 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 500 and BuildingAreaTotal le 1000">
                    500 - 1000 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 1000 and BuildingAreaTotal le 1500">
                    1000 - 1500 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 1500 and BuildingAreaTotal le 2000">
                    1500 - 2000 Sq Ft
                  </option>

                  <option value="BuildingAreaTotal ge 2000">
                    2000+ Sq Ft
                  </option>
                </select>

                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400"
                />
              </div>

              {/* Min */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8b8172]"
                />

                <input
                  value={searchParams.minPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch("minPrice", value ? `ListPrice ge ${value}` : "");
                  }}
                  placeholder="Min Price"
                  className="h-14 w-full border border-[#8b8172]/30 pl-11 px-5 bg-[#fffdfa] text-[#2b2925] outline-none focus:border-[#8b8172]"
                />
              </div>

              {/* Max */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-[#8b8172]"
                />

                <input
                  value={searchParams.maxPrice.replace(/\D/g, "")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");

                    updateSearch("maxPrice", value ? `ListPrice le ${value}` : "");
                  }}
                  placeholder="Max Price"
                  className="h-14 w-full border border-[#8b8172]/30 pl-11 px-5 bg-[#fffdfa] text-[#2b2925] outline-none focus:border-[#8b8172]"
                />
              </div>
            </div>

            <div className="mt-10 flex justify-end gap-4">
              <button
                onClick={clearSearch}
                className="border border-[#8b8172] px-8 py-4 text-xs uppercase tracking-[0.3em] text-[#2b2925] hover:bg-[#2b2925] hover:text-white transition"
              >
                Clear
              </button>

              <button
                onClick={handleSearch}
                className="bg-[#2b2925] px-10 py-4 text-xs uppercase tracking-[0.3em] text-white hover:bg-[#8b8172] transition"
              >
                Search Properties
              </button>
            </div>
          </motion.div>

          {/* LISTING HEADER */}
          <motion.div
            variants={fadeUp}
            className="mt-24 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 border-b border-[#8b8172]/30 pb-10"
          >
            <div>
              <p className="uppercase tracking-[0.45em] text-xs text-[#6d655b] mb-4">
                Property Collection
              </p>

              <h2 className="font-serif text-4xl lg:text-5xl text-[#2b2925]">
                Recent Listings
              </h2>
            </div>

            <select
              value={filter}
              onChange={(e) => {
                setPage(1);
                setFilter(e.target.value);
              }}
              className="rounded-lg border border-[#8b8172]/30 bg-white px-6 py-3 text-sm uppercase tracking-wider text-[#2b2925] outline-none"
            >
              <option value="All">
                All Listings
              </option>

              <option value="Office">
                Office Listings
              </option>

              <option value="Agent">
                Agent Listings
              </option>
            </select>
          </motion.div>


          {/* LISTINGS */}
          {loading && (
            <div className="py-20 text-center">
              <BouncingDotsLoader />
            </div>
          )}

          {!loading && error && (
            <div className="py-20 text-center text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && visibleListings.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-12">
              {
                visibleListings.map(property => (
                  <ListingCard
                    key={property.ListingKey}
                    property={property}
                    // image={images[property.ListingKey]?.[0]}
                    image={property.MainImgUrl} //dummy images
                    type="residential"
                  />
                ))
              }
            </div>
          )}

          {!loading &&
            !error &&
            visibleListings.length === 0 && (
              <div className="py-20 text-center text-[#6d655b] text-lg">
                No properties to show
              </div>
            )}

          <Pagination
            page={page}
            setPage={handlePageChange}
            totalPages={totalPages}
            totalItems={listings.length}
            pageSize={maxListings}
          />
        </motion.div>
      </section>
    </>
  );
}